/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, inject } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { ToastManager } from '@porsche-design-system/components-angular'
import { Subject, catchError, forkJoin, lastValueFrom, of, takeUntil, tap } from 'rxjs'
import { PreferredCommunicationChannel } from './interfaces/preferred-communication-channel.interface'
import { MyProfilePreferredCommunicationChannelService } from './services/myprofile-preferred-communication-channel.service'
import { RESTRICT_GLOBAL_UI_CHANGES } from '../../../pidx-utils/src/lib/helpers/restrictGlobalUIChanges.injectionToken'
import { EmailCommunicationDto, EmailService } from '../../../../apps/myprofile-ui/src/app/services/email/email.service'
import { PublicProfile } from '../../../../apps/myprofile-ui/src/app/services/profile/profile.service'
import { PhoneCommunicationDto } from '../../../../apps/myprofile-ui/src/app/services/phone/phone.service'

@Component({
  selector: 'myprofile-preferred-communication-channel-widget',
  templateUrl: './myprofile-preferred-communication-channel-widget.component.html',
  standalone: false,
  host: { class: 'profile:block profile:relative' },
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MyProfilePreferredCommunicationChannelWidgetComponent implements OnInit, OnDestroy {
  private preferredCommunicationChannelService = inject(MyProfilePreferredCommunicationChannelService)
  private emailService = inject(EmailService)
  private translateService = inject(TranslateService)
  private toastManager = inject(ToastManager)

  @Input() country!: string
  @Input() locale!: string
  // We need to re-fetch the profile data in case it gets updated in another child component
  @Input() set stateChange(_: unknown) {
    this.fetchProfileData()
  }

  public restrictGlobalUIChanges = inject<boolean>(RESTRICT_GLOBAL_UI_CHANGES)
  public profileData?: PublicProfile
  public emailList: EmailCommunicationDto[] = []
  public phoneList: PhoneCommunicationDto[] = []
  public isLoading = true

  private unsubscribe$: Subject<void> = new Subject()

  ngOnInit() {
    this.fetchProfileData()
  }

  ngOnDestroy() {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  async updateCommunicationChannel(_event: Event, channel: PreferredCommunicationChannel) {
    if (this.isValid(channel) && this.preferredCommunicationChannel !== channel) {
      this.isLoading = true
      const payload: PublicProfile = { preferredCommunicationChannel: channel }
      await lastValueFrom(
        this.preferredCommunicationChannelService.updatePreferredCommunicationData(this.country, this.locale, payload).pipe(
          tap((response) => {
            // Service swallows errors and returns null; treat null as failure.
            if (response === null) {
              this.handleSaveError()
              return
            }
            const text =
              payload.preferredCommunicationChannel === 'EMAIL'
                ? this.translateService.instant('pidp.preferredCommChannel.toastMessage.email', { email: this.standardEmail })
                : this.translateService.instant('pidp.preferredCommChannel.toastMessage.mobile', { mobile: this.standardMobile })
            this.toastManager.addMessage({ text, state: 'success' })
            this.fetchProfileData()
          }),
          catchError(() => {
            this.handleSaveError()
            return of(null)
          }),
        ),
      )
    }
  }

  getCommunicationChannelTranslationKeyFor(channel: PreferredCommunicationChannel, key: 'label' | 'missing' | 'unverified'): string {
    return {
      EMAIL: {
        label: 'pidp.personalData.preferredCommunicationChannel.email.label',
        missing: 'pidp.personalData.preferredCommunicationChannel.email.missing',
        unverified: 'pidp.personalData.preferredCommunicationChannel.email.unverified',
      },
      SMS: {
        label: 'pidp.personalData.preferredCommunicationChannel.sms.label',
        missing: 'pidp.personalData.preferredCommunicationChannel.sms.missing',
        unverified: 'pidp.personalData.preferredCommunicationChannel.sms.unverified',
      },
    }?.[channel]?.[key]
  }

  isValid(channel: PreferredCommunicationChannel): boolean {
    if (channel === 'EMAIL') return this.eMailsArePresent && this.standardEmailIsVerified
    else return this.mobilesArePresent && this.standardMobileIsVerified
  }

  /** Resolves to a translation key when a supplementary message exists for the channel, otherwise null. */
  messageKeyFor(channel: PreferredCommunicationChannel): string | null {
    if (channel === 'EMAIL') {
      if (!this.eMailsArePresent) return this.getCommunicationChannelTranslationKeyFor('EMAIL', 'missing')
      if (!this.standardEmailIsVerified) return this.getCommunicationChannelTranslationKeyFor('EMAIL', 'unverified')
      return null
    }
    if (!this.mobilesArePresent) return this.getCommunicationChannelTranslationKeyFor('SMS', 'missing')
    if (!this.standardMobileIsVerified) return this.getCommunicationChannelTranslationKeyFor('SMS', 'unverified')
    return null
  }

  /** Returns the message DOM id when a message is rendered for the channel, otherwise null (so aria-describedby is not set to a dangling id). */
  messageIdFor(channel: PreferredCommunicationChannel): string | null {
    return this.messageKeyFor(channel) ? this.messageDomId(channel) : null
  }

  messageDomId(channel: PreferredCommunicationChannel): string {
    return channel === 'EMAIL' ? 'pref-channel-email-message' : 'pref-channel-sms-message'
  }

  labelDomId(channel: PreferredCommunicationChannel): string {
    return channel === 'EMAIL' ? 'pref-channel-email-label' : 'pref-channel-sms-label'
  }

  groupAriaLabelKey(channel: PreferredCommunicationChannel): string {
    return channel === 'EMAIL'
      ? 'pidp.personalData.preferredCommunicationChannel.email.groupAria'
      : 'pidp.personalData.preferredCommunicationChannel.sms.groupAria'
  }

  get preferredCommunicationChannel() {
    return this.profileData?.preferredCommunicationChannel
  }

  get mobilesArePresent(): boolean {
    return this.phoneList.length !== 0
  }

  get standardMobileIsVerified(): boolean {
    return !!this.phoneList.find((mobile) => mobile.isVerified && mobile.isStandard)
  }

  get standardMobile(): string | undefined {
    return this.phoneList.find((mobile) => mobile.isVerified && mobile.isStandard)?.number
  }

  get eMailsArePresent(): boolean {
    return this.emailList.length !== 0
  }

  get standardEmailIsVerified(): boolean {
    return !!this.emailList.find((email) => email.isVerified && email.isStandard)
  }

  get standardEmail(): string | undefined {
    return this.emailList.find((email) => email.isVerified && email.isStandard)?.email
  }

  get isRestricted(): boolean {
    return ['CN'].includes(this.profileData?.preferredCountry ?? '') && this.restrictGlobalUIChanges
  }

  private fetchProfileData() {
    this.isLoading = true
    // Fetch all three data sources together so that isLoading only flips false
    // once every dependency is resolved. Otherwise the radio group can become
    // enabled with `isValid()` still returning false because one of the lists
    // hasn't arrived yet.
    forkJoin({
      profile: this.preferredCommunicationChannelService.getProfile(this.country, this.locale),
      emails: this.emailService.getEmailsForProfile(this.country, this.locale),
      phones: this.preferredCommunicationChannelService.getPhoneList(this.country, this.locale),
    })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: ({ profile, emails, phones }) => {
          this.profileData = profile ?? undefined
          this.emailList = emails ?? []
          this.phoneList = phones ?? []
          this.isLoading = false
        },
        error: () => {
          this.isLoading = false
        },
      })
  }

  private handleSaveError(): void {
    this.isLoading = false
    const text = this.translateService.instant('pidp.personalData.preferredCommunicationChannel.error.toast')
    this.toastManager.addMessage({ text, state: 'error' })
  }
}
